// Phase 6.1 확장 · LIVE 판매자 미니쇼핑몰 (커버 + LIVE MODE + 노출 옵션)
// 순서: HEADER → (LIVE + 방송상품 + 지금소개중) → 쇼츠 → 다음방송 → 공동구매 → 쿠폰 → 전체상품 → 상세정보
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Instagram, Youtube, Music, BellRing, Share2, ShieldCheck,
  Play, Clock, Radio, ShoppingBag, ChevronRight, Users, Ticket, MessageSquare, Settings,
} from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import NowSellingCard from '@/components/NowSellingCard';
import ConsumerInquiryModal from '@/components/ConsumerInquiryModal';
import { SellerBroadcastSchedule, SellerReplayList } from '@/components/seller-profile/SellerBroadcastSchedule';

const PLATFORM_META = {
  instagram: { name: 'Instagram', icon: Instagram, url: (h) => (/^https?:/i.test(h) ? h : `https://instagram.com/${h}`) },
  tiktok:    { name: 'TikTok',    icon: Music,     url: (h) => (/^https?:/i.test(h) ? h : `https://tiktok.com/@${h}`) },
  youtube:   { name: 'YouTube',   icon: Youtube,   url: (h) => (/^https?:/i.test(h) ? h : `https://youtube.com/@${h}`) },
};
const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

const SellerProfile = ({ user }) => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [weekLives, setWeekLives] = useState([]);
  const [products, setProducts] = useState([]);
  const [liveProducts, setLiveProducts] = useState([]);
  const [liveState, setLiveState] = useState({ product_visibility_mode: 'all', active_product_id: null });
  const [clips, setClips] = useState([]);
  const [gbs, setGbs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [nowBcast, setNowBcast] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [showAllLive, setShowAllLive] = useState(false);
  const [expandAllProducts, setExpandAllProducts] = useState(false);

  // STEP 4 · 프로필 진입 시 항상 최상단 (대표 커버 → 판매자 정보 순 노출).
  // 같은 페이지 내 anchor 이동은 replace 아니므로 방해하지 않음.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sellerId]);
  const [shareToast, setShareToast] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const [s, lives, prods, cls, g, cp, today] = await Promise.all([
          api.get(`/sellers/${sellerId}`),
          api.get('/lives/week').catch(() => ({ data: [] })),
          api.get(`/sellers/${sellerId}/products`).catch(() => ({ data: [] })),
          api.get(`/public/sellers/${sellerId}/short-clips?limit=12`).catch(() => ({ data: { items: [] } })),
          api.get(`/public/sellers/${sellerId}/group-buys?limit=6`).catch(() => ({ data: { items: [] } })),
          api.get(`/public/sellers/${sellerId}/coupons?limit=6`).catch(() => ({ data: { items: [] } })),
          api.get(`/public/sellers/${sellerId}/today-broadcast`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setSeller(s.data);
        setFollowerCount(s.data?.follower_count || 0);
        setWeekLives((lives.data || []).filter((l) => l.seller_id === sellerId));
        setProducts(prods.data || []);
        setClips(cls.data?.items || []);
        setGbs(g.data?.items || []);
        setCoupons(cp.data?.items || []);
        setNowBcast(today.data?.broadcast || null);
        if (user) {
          try { const f = await api.get(`/is-following/${sellerId}`); if (!cancelled) setIsFollowing(!!f.data?.is_following); } catch {}
        }
      } catch (e) { if (e?.response?.status === 404) setNotFound(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sellerId, user]);

  // ── 실시간 방송 상품 상태 동기화 (broadcast 기반, 4초 polling) ─────────
  useEffect(() => {
    const bid = nowBcast?.schedule_id;
    if (!bid) { setLiveProducts([]); setLiveState({ product_visibility_mode: 'all', active_product_id: null }); return; }
    let cancelled = false;
    let timer;
    const tick = async () => {
      try {
        const r = await api.get(`/public/broadcasts/${bid}/live-products`);
        if (cancelled) return;
        setLiveProducts(r.data?.items || []);
        setLiveState({
          product_visibility_mode: r.data?.product_visibility_mode || 'all',
          active_product_id: r.data?.active_product_id || null,
        });
      } catch {}
      if (!cancelled) timer = setTimeout(tick, 4000);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [nowBcast?.schedule_id]);

  const nowLive = useMemo(() => weekLives.find((l) => l.status === 'live'), [weekLives]);
  const nextLive = useMemo(() => {
    const now = new Date();
    return weekLives
      .filter((l) => l.status !== 'live' && l.status !== 'ended' && new Date(l.scheduled_at) >= now)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
  }, [weekLives]);
  const nextLives = useMemo(() => {
    const now = new Date();
    return weekLives
      .filter((l) => l.status !== 'live' && l.status !== 'ended' && new Date(l.scheduled_at) >= now)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
      .slice(0, 3);
  }, [weekLives]);

  const cfg = seller?.profile_home_config || {};
  const showLiveProducts = cfg.show_live_products !== false;
  const allProductsMode = cfg.all_products_mode || 'always';
  // "LIVE 중" 판별 — 오늘 편성이 존재하면 LIVE 상태로 간주 (기존 LIVE 페이지와 동일한 Source of Truth)
  const isLiveActive = !!nowBcast?.schedule_id || !!nowLive;
  const showAllProducts = allProductsMode === 'always' || (allProductsMode === 'live_only' && isLiveActive);

  const handleFollow = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isFollowing) { await api.post('/unfollow', null, { params: { seller_id: sellerId } }); setIsFollowing(false); setFollowerCount((c) => Math.max(0, c - 1)); }
      else { await api.post('/follow', null, { params: { seller_id: sellerId } }); setIsFollowing(true); setFollowerCount((c) => c + 1); }
    } catch {}
  };
  const handleShare = async () => {
    const url = window.location.href;
    const title = seller?.name ? `${seller.name} - LIVEMOA` : 'LIVEMOA';
    if (navigator.share) { try { await navigator.share({ title, url }); return; } catch {} }
    try { await navigator.clipboard.writeText(url); setShareToast('링크가 복사되었습니다'); } catch { setShareToast('공유 실패'); }
    setTimeout(() => setShareToast(''), 2500);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]"/></div>;
  if (!seller || notFound) return <div className="min-h-screen flex items-center justify-center"><div className="text-center" data-testid="seller-not-found"><h2 className="text-xl font-bold mb-2">판매자를 찾을 수 없습니다.</h2><Link to="/" className="text-[#FF3B30]">홈으로</Link></div></div>;

  const platforms = Object.entries(seller.platforms || {}).filter(([k, v]) => PLATFORM_META[k] && v && String(v).trim());
  const isOwner = user?.seller_id === sellerId;
  const cover = seller.cover_image_url;
  const tagline = seller.tagline || ''; // bio 사용 안 함 · 하단 상세정보로 분리
  const isLiveMode = isLiveActive;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="seller-profile-page">
      {shareToast && <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-black text-white text-sm font-medium rounded-full shadow-lg" data-testid="share-toast">{shareToast}</div>}

      {/* ─── 브랜드 커버 + 프로필 ────────────────────────────── */}
      <section className="relative" data-testid="seller-cover-section">
        <div className="relative w-full aspect-[16/6] sm:aspect-[16/5] bg-gradient-to-br from-[#FF3B30]/20 via-[#FF9F0A]/10 to-[#FFD60A]/5 overflow-hidden">
          {cover && <SafeImg src={cover} kind="seller_cover" alt="cover" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: 'center 30%' }} data-testid="seller-cover-image"/>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"/>
          {isLiveMode && (
            <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black shadow-lg animate-pulse inline-flex items-center gap-1" data-testid="live-mode-badge">
              <Radio size={12}/> LIVE MODE
            </div>
          )}
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 sm:-mt-14 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <SafeImg src={seller.profile_photo} kind="seller_profile" alt={seller.name}
              className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-4 border-white dark:border-[#0A0A0A] shadow-lg object-cover shrink-0" data-testid="seller-profile-photo"/>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-black tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="seller-profile-name">{seller.name}</h1>
                {seller.is_approved && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 text-blue-600 text-[10px] font-bold rounded-full" data-testid="verified-badge"><ShieldCheck size={10}/> 공식 셀러</span>}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span data-testid="livemoa-follower-count"><b className="text-sm font-black text-[#FF3B30]" style={{ fontFamily: 'Manrope, sans-serif' }}>{followerCount.toLocaleString()}</b> <span className="text-gray-500">팔로워</span></span>
                <span className="text-gray-300">·</span>
                <span><b className="text-sm font-black">{weekLives.length}</b> <span className="text-gray-500">이번 주 방송</span></span>
                <span className="text-gray-300">·</span>
                <span><b className="text-sm font-black">{products.length}</b> <span className="text-gray-500">상품</span></span>
              </div>
            </div>
          </div>

          {/* Tagline 1줄 · 프로필 상단 · bio 큰 박스 제거 */}
          <div className="mt-3">
            {tagline && (
              <p className="text-sm text-gray-700 dark:text-gray-300 truncate" data-testid="seller-tagline">{tagline}</p>
            )}
          </div>

          {/* CTA 버튼들 */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button onClick={handleFollow}
              className={`inline-flex items-center gap-1 px-5 py-2 rounded-full font-black text-xs transition-colors ${isFollowing ? 'bg-black/10 dark:bg-white/10 text-black dark:text-white' : 'bg-[#FF3B30] text-white hover:bg-[#D32F2F]'}`} data-testid="follow-button">
              {isFollowing ? '팔로잉 중' : '+ 팔로우'}
            </button>
            <button onClick={() => navigate(`/seller/notifications?sid=${sellerId}`)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full font-bold text-xs bg-white dark:bg-[#141414] border border-black/10 hover:border-[#FF3B30]" data-testid="notification-button"><BellRing size={12}/> 알림</button>
            <button onClick={handleShare} className="inline-flex items-center gap-1 px-4 py-2 rounded-full font-bold text-xs bg-white dark:bg-[#141414] border border-black/10 hover:border-[#FF3B30]" data-testid="share-button"><Share2 size={12}/> 공유</button>
            <button onClick={() => setAskOpen(true)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full font-bold text-xs bg-white dark:bg-[#141414] border border-black/10 hover:border-[#FF3B30]" data-testid="seller-ask-inquiry"><MessageSquare size={12}/> 문의</button>
            {isOwner && (
              <div className="ml-auto flex items-center gap-2">
                <Link to="/seller/dashboard?section=profile" className="inline-flex items-center gap-1 px-3 py-2 rounded-full font-bold text-xs bg-white dark:bg-[#141414] border border-black/10 hover:border-[#FF3B30]" data-testid="profile-settings-btn">
                  <Settings size={12}/> 프로필 관리
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-8">

        {/* ② 현재 LIVE — 방송 중일 때만 */}
        {nowLive && (
          <section data-testid="live-section" className="rounded-2xl overflow-hidden border-2 border-[#FF3B30] bg-white dark:bg-[#141414]">
            <div className="relative aspect-video bg-gray-100">
              <SafeImg src={nowLive.thumbnail} kind="broadcast" alt={nowLive.title} className="w-full h-full object-cover"/>
              <span className="absolute top-3 left-3 px-3 py-1 bg-[#FF3B30] text-white text-xs font-black rounded-full animate-pulse inline-flex items-center gap-1" data-testid="live-badge"><Radio size={12}/> 지금 LIVE</span>
            </div>
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-base font-black truncate">{nowLive.title}</p>
                <p className="text-xs text-gray-500 capitalize">{nowLive.platform} · 시작 {format(new Date(nowLive.scheduled_at), 'HH:mm')}</p>
              </div>
              <a href={nowLive.stream_url || '#'} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-black text-xs inline-flex items-center gap-1" data-testid="watch-live-button"><Play size={12} fill="currentColor"/> 방송보기</a>
              {liveProducts.length > 0 && showLiveProducts && (
                <Link to={`/seller/${sellerId}/live-products`} className="px-4 py-2 rounded-full bg-black text-white font-black text-xs inline-flex items-center gap-1" data-testid="live-products-btn"><ShoppingBag size={12}/> 오늘의 라이브 상품</Link>
              )}
            </div>
          </section>
        )}

        {/* 🔴 방송 정보 한 줄 · 지금 소개중 위 · LIVE bar 전체 클릭 가능 (기존 stream_url REUSE) */}
        {nowBcast?.schedule_id && (() => {
          const watchUrl = nowBcast.stream_url || nowBcast.external_url || null;
          const statusStr = String(nowBcast.status || '').toLowerCase();
          const isLive = ['live', '라이브'].includes(statusStr);
          const isScheduled = ['예정', 'scheduled'].includes(statusStr);
          const watchLabel = isLive ? '방송보기' : (isScheduled ? '방송 예정' : '방송보기');
          const inner = (
            <>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${isLive ? 'bg-[#FF3B30] text-white animate-pulse' : 'bg-gray-500 text-white'}`}>
                <Radio size={10}/> {isLive ? 'LIVE' : (isScheduled ? '예정' : 'LIVE')}
              </span>
              <span className="text-sm font-bold truncate" data-testid="live-info-title">{nowBcast.title || '진행 중인 방송'}</span>
              {nowBcast.platform && <span className="text-xs text-gray-500 capitalize" data-testid="live-info-platform">· {nowBcast.platform}</span>}
              {watchUrl ? (
                <span
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FF3B30] group-hover:bg-[#D32F2F] text-white text-[11px] font-black"
                  data-testid="live-info-watch">
                  <Play size={10} fill="currentColor"/> {watchLabel} <ChevronRight size={10}/>
                </span>
              ) : (
                <span className="ml-auto text-[11px] text-gray-400 font-bold" data-testid="live-info-nowatch">
                  방송 링크 준비중
                </span>
              )}
            </>
          );
          return watchUrl ? (
            <a
              href={watchUrl}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-2 flex-wrap px-3 py-2 rounded-full bg-[#FF3B30]/5 border border-[#FF3B30]/20 hover:bg-[#FF3B30]/10 hover:border-[#FF3B30]/40 transition-colors cursor-pointer"
              data-testid="live-info-bar"
              aria-label={`${nowBcast.title || '방송'} ${watchLabel}`}
            >
              {inner}
            </a>
          ) : (
            <div
              className="flex items-center gap-2 flex-wrap px-3 py-2 rounded-full bg-[#FF3B30]/5 border border-[#FF3B30]/20"
              data-testid="live-info-bar"
            >
              {inner}
            </div>
          );
        })()}

        {/* ③ 지금 소개중 — LIVE 페이지와 동일한 nowBcast 사용 */}
        {nowBcast?.schedule_id && <NowSellingCard broadcastId={nowBcast.schedule_id} variant="inline"/>}

        <SellerBroadcastSchedule sellerId={sellerId} />
        <SellerReplayList sellerId={sellerId} />

        {/* ④ 오늘의 라이브 상품 — 오늘 편성 + show_live_products=true + 상품 있음 */}
        {nowBcast?.schedule_id && showLiveProducts && liveProducts.length > 0 && (
          <section id="live-products" data-testid="live-products-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-black tracking-tight inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Radio className="text-[#FF3B30]" size={18}/> 🔴 오늘의 라이브 상품 <span className="text-sm text-gray-400">{liveProducts.length}개</span>
              </h2>
              <Link to={`/seller/${sellerId}/products`} className="text-xs font-bold text-[#FF3B30] inline-flex items-center gap-0.5" data-testid="live-products-viewall">전체상품 보기 <ChevronRight size={12}/></Link>
            </div>
            {/* PC: 6열 Compact Grid · Mobile: 가로 Swipe (2.3개) · 12개 초과 시 안전 더보기 */}
            <div className="hidden sm:grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {(showAllLive ? liveProducts : liveProducts.slice(0, 12)).map((p) => {
                const introOnly = liveState.product_visibility_mode === 'introduced_only';
                const isActive  = liveState.active_product_id === p.product_id;
                const hidden    = introOnly && !p.is_introduced;
                return (
                  <Link to={hidden ? '#' : `/product/${p.product_id}`}
                    onClick={(e) => { if (hidden) e.preventDefault(); }}
                    key={p.product_id}
                    className={`rounded-lg overflow-hidden border bg-white dark:bg-[#141414] transition-all ${
                      isActive ? 'border-[#FF3B30] shadow-sm' : 'border-black/5 dark:border-white/10'
                    } ${hidden ? 'cursor-not-allowed' : 'hover:-translate-y-0.5 hover:shadow'}`}
                    data-testid={`live-product-${p.product_id}`}>
                    <div className="relative aspect-square bg-gray-100 overflow-hidden">
                      <SafeImg src={p.image} kind="product" alt={p.name}
                        className={`w-full h-full object-cover ${hidden ? 'blur-sm scale-105' : ''}`}/>
                      {isActive && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[8px] font-black animate-pulse inline-flex items-center gap-0.5" data-testid={`active-badge-${p.product_id}`}>
                          ● 소개중
                        </span>
                      )}
                      {hidden && (
                        <div className="absolute inset-0 flex items-center justify-center" data-testid={`soon-overlay-${p.product_id}`}>
                          <span className="px-2 py-0.5 rounded-full bg-black/70 text-white text-[9px] font-black">곧 소개</span>
                        </div>
                      )}
                      {!isActive && !hidden && introOnly && p.is_introduced && (
                        <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-gray-800/80 text-white text-[8px] font-bold">소개 완료</span>
                      )}
                    </div>
                    <div className="p-1.5">
                      <p className="text-[11px] font-medium line-clamp-1">{p.name}</p>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        {p.discount_rate > 0 && <span className="text-[#FF3B30] text-[9px] font-black">-{p.discount_rate}%</span>}
                        <span className="text-[11px] font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                      </div>
                      {hidden && <p className="text-[8px] text-gray-400 mt-0.5">소개 후 구매 가능</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="sm:hidden flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="live-products-swipe">
              {liveProducts.map((p) => {
                const introOnly = liveState.product_visibility_mode === 'introduced_only';
                const isActive  = liveState.active_product_id === p.product_id;
                const hidden    = introOnly && !p.is_introduced;
                return (
                  <Link to={hidden ? '#' : `/product/${p.product_id}`}
                    onClick={(e) => { if (hidden) e.preventDefault(); }}
                    key={`m-${p.product_id}`}
                    className={`shrink-0 snap-start w-[42%] rounded-lg overflow-hidden border bg-white dark:bg-[#141414] ${
                      isActive ? 'border-[#FF3B30]' : 'border-black/5 dark:border-white/10'
                    } ${hidden ? 'cursor-not-allowed' : ''}`}
                    data-testid={`live-product-m-${p.product_id}`}>
                    <div className="relative aspect-square bg-gray-100 overflow-hidden">
                      <SafeImg src={p.image} kind="product" alt={p.name}
                        className={`w-full h-full object-cover ${hidden ? 'blur-sm scale-105' : ''}`}/>
                      {isActive && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[8px] font-black animate-pulse">● 소개중</span>
                      )}
                      {hidden && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="px-2 py-0.5 rounded-full bg-black/70 text-white text-[9px] font-black">곧 소개</span>
                        </div>
                      )}
                    </div>
                    <div className="p-1.5">
                      <p className="text-[11px] font-medium line-clamp-1">{p.name}</p>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        {p.discount_rate > 0 && <span className="text-[#FF3B30] text-[9px] font-black">-{p.discount_rate}%</span>}
                        <span className="text-[11px] font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                      </div>
                      {hidden && <p className="text-[8px] text-gray-400 mt-0.5">소개 후 구매 가능</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
            {/* PC 전용 더보기 · Mobile 은 swipe 로 이미 접근 가능 */}
            {liveProducts.length > 12 && !showAllLive && (
              <div className="hidden sm:flex justify-center mt-3">
                <button onClick={() => setShowAllLive(true)}
                  className="px-4 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold hover:border-[#FF3B30]"
                  data-testid="live-products-show-all">
                  더보기 ({liveProducts.length - 12}개 남음) <ChevronRight size={12} className="inline"/>
                </button>
              </div>
            )}
          </section>
        )}

        {/* ⑤ 내 쇼츠 */}
        {clips.length > 0 && (
          <section data-testid="seller-clips-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>🎬 내 쇼츠</h2>
              <Link to="/short-clips" state={{ sellerScope: true, sellerId }} className="text-xs font-bold text-[#FF3B30] inline-flex items-center gap-0.5" data-testid="clips-viewall">전체보기 <ChevronRight size={12}/></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {clips.slice(0, 10).map((c) => (
                <Link key={c.clip_id} to="/short-clips" state={{ initialClip: c, sellerScope: true, sellerId: seller?.seller_id || c.seller_id }} className="relative aspect-[9/16] rounded-xl overflow-hidden bg-gray-100 dark:bg-white/5 group shadow-sm hover:shadow-lg transition-shadow" data-testid={`clip-thumb-${c.clip_id}`}>
                  <SafeImg src={c.thumbnail_url} kind="broadcast" alt={c.title || 'shorts'} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
                  <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                    {c.platform === 'youtube' && <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black" data-testid={`clip-platform-badge-${c.clip_id}`}>YouTube</span>}
                    {c.platform === 'tiktok'  && <span className="px-1.5 py-0.5 rounded-full bg-black text-white text-[9px] font-black" data-testid={`clip-platform-badge-${c.clip_id}`}>TikTok</span>}
                    {c.platform === 'instagram' && <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white text-[9px] font-black" data-testid={`clip-platform-badge-${c.clip_id}`}>Instagram</span>}
                    {c.is_featured && <span className="px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">★</span>}
                  </div>
                  <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow-md"><Play size={12} className="text-black ml-0.5" fill="currentColor"/></div>
                  {c.product_snapshot?.name && <p className="absolute inset-x-0 bottom-0 p-2 text-[10px] font-bold text-white truncate">{c.product_snapshot.name}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ⑥ 다음 방송 — 내 쇼츠 바로 아래 */}
        {nextLives.length > 0 && (
          <section data-testid="next-live-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-black tracking-tight inline-flex items-center gap-1" style={{ fontFamily: 'Manrope, sans-serif' }}>📅 다음 방송</h2>
              <Link to="/schedule" className="text-xs font-bold text-[#FF3B30] inline-flex items-center gap-0.5" data-testid="schedule-viewall">전체 일정 보기 <ChevronRight size={12}/></Link>
            </div>
            <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] divide-y divide-black/5 dark:divide-white/10 overflow-hidden">
              {nextLives.map((live) => (
                <div key={live.schedule_id} className="flex items-center gap-3 p-3 hover:bg-black/5 dark:hover:bg-white/5" data-testid={`next-live-row-${live.schedule_id}`}>
                  <div className="flex-shrink-0 text-center w-14">
                    <p className="text-[10px] font-bold text-[#FF3B30]">{format(new Date(live.scheduled_at), 'M.d', { locale: ko })}</p>
                    <p className="text-lg font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(new Date(live.scheduled_at), 'HH:mm')}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black truncate">{live.title}</p>
                    <p className="text-[11px] text-gray-500 capitalize">{live.platform}</p>
                  </div>
                  <button onClick={() => navigate(`/seller/notifications?sid=${sellerId}`)} className="px-3 py-1.5 rounded-full bg-white dark:bg-[#0A0A0A] border border-black/10 hover:border-[#FF3B30] font-bold text-[11px] inline-flex items-center gap-1" data-testid={`next-live-notify-${live.schedule_id}`}><BellRing size={11}/> 알림</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ⑦ 진행중 공동구매 */}
        {gbs.length > 0 && (
          <section data-testid="seller-gb-section">
            <h2 className="text-lg sm:text-xl font-black tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>🔥 진행중 공동구매</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {gbs.map((gb) => {
                const pct = gb.target > 0 ? Math.min(100, Math.round((gb.current_count / gb.target) * 100)) : 0;
                const savedPct = gb.base_price > 0 ? Math.max(0, Math.round(((gb.base_price - gb.current_price) / gb.base_price) * 100)) : 0;
                return (
                  <Link key={gb.group_buy_id} to={`/product/${gb.product_id}`} className="flex gap-3 p-3 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:shadow-md" data-testid={`gb-card-${gb.group_buy_id}`}>
                    <div className="w-20 h-20 rounded-lg bg-gray-100 shrink-0 overflow-hidden">
                      {gb.product_image && <img src={gb.product_image} alt="" className="w-full h-full object-cover"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{gb.product_name}</p>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className="text-sm font-black text-[#FF3B30]">{won(gb.current_price)}</span>
                        {gb.base_price > gb.current_price && <span className="text-[10px] line-through text-gray-400">{won(gb.base_price)}</span>}
                        {savedPct > 0 && <span className="text-[10px] font-black text-[#FF3B30]">-{savedPct}%</span>}
                      </div>
                      <div className="h-1.5 rounded-full bg-black/5 mt-2 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FF3B30] to-[#FF9F0A]" style={{ width: `${pct}%` }}/>
                      </div>
                      <div className="flex items-center justify-between mt-1 text-[10px]">
                        <span className="font-bold text-[#FF3B30] inline-flex items-center gap-0.5"><Users size={9}/> {gb.current_count}/{gb.target}명</span>
                        <span className="text-gray-500">{pct}% 달성</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ⑧ 쿠폰 */}
        {coupons.length > 0 && (
          <section data-testid="seller-coupons-section">
            <h2 className="text-lg sm:text-xl font-black tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>🎟 판매자 혜택</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {coupons.map((c) => {
                const label = c.kind === 'free_ship' ? '무료배송' : c.kind === 'percent' ? `${c.discount_value}%` : `${(c.discount_value).toLocaleString()}원`;
                return (
                  <div key={c.coupon_id} className="rounded-2xl border-2 border-dashed border-[#FF3B30]/40 bg-gradient-to-br from-[#FF3B30]/5 to-transparent p-3 flex items-center gap-3" data-testid={`coupon-${c.coupon_id}`}>
                    <Ticket className="text-[#FF3B30] shrink-0" size={22}/>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-black text-[#FF3B30]" style={{ fontFamily: 'Manrope, sans-serif' }}>{label} 할인</p>
                      <p className="text-[10px] text-gray-500 truncate">{c.name}{c.min_order_amount > 0 && ` · ${c.min_order_amount.toLocaleString()}원 이상`}</p>
                    </div>
                    <button className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black hover:bg-[#D32F2F]" data-testid={`coupon-btn-${c.coupon_id}`}>받기</button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ⑨ 전체 상품 — all_products_mode 반영 */}
        {showAllProducts && products.length > 0 && (
          <section id="products-section" data-testid="products-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>🛍 전체 상품 <span className="text-sm text-gray-400 ml-1">{products.length}</span></h2>
              <div className="inline-flex items-center gap-2">
                {allProductsMode === 'live_only' && nowLive && <span className="text-[10px] font-bold text-[#FF3B30] inline-flex items-center gap-0.5"><Radio size={10}/> LIVE 중에만 노출</span>}
                <Link
                  to={`/seller/${sellerId}/products`}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black shadow-sm transition-colors"
                  data-testid="all-products-viewall"
                  aria-label="판매자 전체상품 페이지로 이동">
                  전체상품 보기 <ChevronRight size={12}/>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
              {(expandAllProducts ? products : products.slice(0, 15)).map((p) => {
                const isLiveOnly = p.sale_mode === 'live_only';
                const canBuy = !isLiveOnly || isLiveActive;
                return (
                  <Link to={`/product/${p.product_id}`} key={p.product_id} className={`rounded-lg overflow-hidden border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] hover:-translate-y-0.5 hover:shadow transition-all ${!canBuy ? 'opacity-70' : ''}`} data-testid="product-card">
                    <div className="relative aspect-square bg-gray-100 overflow-hidden">
                      <SafeImg src={p.image} kind="product" alt={p.name} className="w-full h-full object-cover"/>
                      {isLiveOnly && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black inline-flex items-center gap-0.5" data-testid={`live-only-badge-${p.product_id}`}>
                          <Radio size={8}/> {canBuy ? 'LIVE 중 구매' : 'LIVE 중만'}
                        </span>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium line-clamp-2 min-h-[2rem]">{p.name}</p>
                      <div className="flex items-baseline gap-1 mt-1">
                        {p.discount_rate > 0 && <span className="text-[#FF3B30] text-[10px] font-black">-{p.discount_rate}%</span>}
                        <span className="text-xs font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                      </div>
                      {isLiveOnly && !canBuy && <p className="text-[9px] text-gray-400 mt-0.5">방송 시작 시 구매 가능</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
            {products.length > 15 && !expandAllProducts && (
              <div className="flex justify-center mt-3">
                <button onClick={() => setExpandAllProducts(true)}
                  className="px-4 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold hover:border-[#FF3B30]"
                  data-testid="products-show-all">
                  더보기 ({products.length - 15}개 남음) <ChevronRight size={12} className="inline"/>
                </button>
              </div>
            )}
          </section>
        )}

        {/* ⑫ 판매자 상세정보 · 접기/펼치기 (프로필 최하단) */}
        {(seller.bio || platforms.length > 0 || seller.contact_email || seller.announcements) && (
          <section data-testid="seller-info-section" className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] overflow-hidden">
            <button onClick={() => setMoreOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              data-testid="seller-info-toggle" aria-expanded={moreOpen}>
              <span className="text-sm font-black inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                판매자 상세정보
                <span className="text-[10px] font-bold text-gray-400">사업자·고객센터·통신판매 정보 등</span>
              </span>
              <ChevronRight size={16} className={`transition-transform ${moreOpen ? 'rotate-90' : ''}`}/>
            </button>
            {moreOpen && (
              <div className="px-5 pb-5 space-y-4 border-t border-black/5 dark:border-white/10 pt-4" data-testid="seller-info-body">
                {seller.announcements && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">📢 공지사항</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line">{seller.announcements}</p>
                  </div>
                )}
                {seller.bio && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">사업자 · 판매자 정보</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line">{seller.bio}</p>
                  </div>
                )}
                {seller.broadcast_schedule_intro && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">방송 일정</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line">{seller.broadcast_schedule_intro}</p>
                  </div>
                )}
                {seller.contact_email && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">고객 문의</p>
                    <a href={`mailto:${seller.contact_email}`} className="text-xs font-bold text-[#FF3B30] hover:underline">{seller.contact_email}</a>
                  </div>
                )}
                {platforms.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">SNS / 외부 채널</p>
                    <div className="flex flex-wrap gap-2">
                      {platforms.map(([k, h]) => {
                        const meta = PLATFORM_META[k]; const Icon = meta.icon;
                        return <a key={k} href={meta.url(h)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 hover:border-[#FF3B30] text-xs font-bold" data-testid={`platform-chip-${k}`}><Icon size={12}/> {meta.name}</a>;
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-gray-400">배송/교환/반품 상세 안내는 상품 상세 페이지에서 확인해주세요.</p>
              </div>
            )}
          </section>
        )}
      </div>

      <ConsumerInquiryModal open={askOpen} onClose={() => setAskOpen(false)} scope="seller" context={{ seller_id: sellerId, title: seller.brand_name || seller.name }} defaultCategory="other"/>
    </div>
  );
};

export default SellerProfile;
